/**
 * Trip Import Service
 *
 * Handles complete trip import from ZIP and JSON files
 * Provides validation, preview, and import functionality with conflict resolution
 */

import zipProcessor from '../formats/zip';

class TripImportService {
  constructor() {
    this.supportedFormats = ['zip', 'json'];
    this.maxFileSize = 50 * 1024 * 1024; // 50MB limit
    this.supportedVersions = ['1.0.0'];
    this.notifications = {
      started: null,
      completed: null,
      error: null
    };
  }

  /**
   * Preview import file and validate contents
   *
   * @param {File} file - Import file (ZIP or JSON)
   * @returns {Promise<Object>} Import preview data
   */
  async previewImport(file) {
    try {
      console.log('Previewing import file:', file.name);

      // Validate file basic properties
      const fileValidation = this.validateImportFile(file);
      if (!fileValidation.valid) {
        throw new Error(fileValidation.error);
      }

      const fileExtension = file.name.split('.').pop().toLowerCase();
      let importData;

      if (fileExtension === 'zip') {
        importData = await this.previewZipImport(file);
      } else if (fileExtension === 'json') {
        importData = await this.previewJsonImport(file);
      } else {
        throw new Error(`Unsupported file type: ${fileExtension}`);
      }

      // Validate import data structure
      const validation = await this.validateImportData(importData);

      return {
        ...importData,
        validation,
        fileInfo: {
          name: file.name,
          size: file.size,
          type: fileExtension,
          lastModified: file.lastModified
        }
      };

    } catch (error) {
      console.error('Import preview failed:', error);
      throw error;
    }
  }

  /**
   * Import trip data using simplified single-mode approach
   * Backend automatically handles: check if trip exists → confirm if needed → import with new IDs
   *
   * @param {File} file - Import file
   * @param {Object} importData - Preview data
   * @param {Object} context - Application context (must include { user, controller })
   * @returns {Promise<Object>} Import result
   */
  async importTrip(file, importData, context) {
    try {
      console.log('[TripImportService] Starting simplified import...');

      this.showNotification('started', file.name);

      // Validate context (requires user and controller)
      if (!context || !context.user) {
        throw new Error('Invalid import context - user required');
      }

      if (!context.controller) {
        throw new Error('Invalid import context - controller required');
      }

      // Call TripImportController to handle import with simplified logic
      // Controller will: check existing trip → confirm replacement → import with new IDs
      // CRITICAL: Pass complete importData including zipInstance for document file uploads
      const result = await context.controller.importTripData(importData, context);

      this.showNotification('completed', result);
      return { success: true, result };

    } catch (error) {
      console.error('Import failed:', error);
      this.showNotification('error', error.message);
      return { success: false, error: error.message };
    }
  }

  /**
   * Preview ZIP import
   */
  async previewZipImport(file) {
    try {
      const extractedData = await zipProcessor.extractTripImport(file);

      return {
        manifest: extractedData.manifest,
        tripData: extractedData.tripData,
        documentInfo: extractedData.documentInfo,
        contentSummary: this.generateContentSummary(extractedData.tripData),
        zipInstance: extractedData.zipInstance
      };

    } catch (error) {
      throw new Error(`Failed to process ZIP file: ${error.message}`);
    }
  }

  /**
   * Preview JSON import
   */
  async previewJsonImport(file) {
    try {
      const fileContent = await this.readFileAsText(file);
      const jsonData = JSON.parse(fileContent);

      // Determine if this is a single trip or export structure
      let tripData;
      let manifest = null;

      if (jsonData.trip || jsonData.days || jsonData.places) {
        // Direct trip data format
        tripData = jsonData;
      } else if (jsonData.tripData) {
        // Export format with metadata
        tripData = jsonData.tripData;
        manifest = jsonData.manifest || jsonData.exportMetadata;
      } else {
        throw new Error('Unrecognized JSON format');
      }

      return {
        manifest,
        tripData,
        documentInfo: { documents: [], media: [] },
        contentSummary: this.generateContentSummary(tripData)
      };

    } catch (error) {
      if (error instanceof SyntaxError) {
        throw new Error('Invalid JSON format');
      }
      throw new Error(`Failed to process JSON file: ${error.message}`);
    }
  }

  /**
   * Import as new trip
   */
  async importAsNewTrip(importData, context) {
    console.log('Importing as new trip...');

    // In a real implementation, this would:
    // 1. Create new trip in database
    // 2. Import all entities with new IDs
    // 3. Preserve relationships between entities
    // 4. Import documents if available

    // Simulate import process
    await this.simulateImportProcess('new');

    const newTripId = `imported-${Date.now()}`;
    const tripName = importData.tripData.trip?.name || 'Imported Trip';

    return {
      tripId: newTripId,
      tripName: `${tripName} (Imported)`,
      itemsImported: this.countImportedItems(importData),
      documentsImported: importData.documentInfo?.documents?.length || 0,
      mode: 'new'
    };
  }

  /**
   * Merge with current trip
   */
  async mergeWithCurrentTrip(importData, context) {
    console.log('Merging with current trip...');

    // In a real implementation, this would:
    // 1. Check for duplicate entities
    // 2. Add new entities to existing trip
    // 3. Handle conflicts appropriately
    // 4. Update relationships

    await this.simulateImportProcess('merge');

    return {
      tripId: context.tripId,
      tripName: context.trip.name,
      itemsImported: this.countImportedItems(importData),
      itemsSkipped: Math.floor(this.countImportedItems(importData) * 0.2), // 20% simulated duplicates
      documentsImported: importData.documentInfo?.documents?.length || 0,
      mode: 'merge'
    };
  }

  /**
   * Replace current trip
   */
  async replaceCurrentTrip(importData, context) {
    console.log('Replacing current trip...');

    // In a real implementation, this would:
    // 1. Backup existing trip data
    // 2. Clear current trip entities
    // 3. Import new data with current trip ID
    // 4. Update trip metadata

    await this.simulateImportProcess('replace');

    return {
      tripId: context.tripId,
      tripName: importData.tripData.trip?.name || context.trip.name,
      itemsImported: this.countImportedItems(importData),
      itemsReplaced: await this.getCurrentTripItemCount(context),
      documentsImported: importData.documentInfo?.documents?.length || 0,
      mode: 'replace'
    };
  }

  /**
   * Validation methods
   */
  validateImportFile(file) {
    // Check file size
    if (file.size > this.maxFileSize) {
      return {
        valid: false,
        error: `File too large. Maximum size is ${Math.round(this.maxFileSize / 1024 / 1024)}MB`
      };
    }

    // Check file type
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!this.supportedFormats.includes(fileExtension)) {
      return {
        valid: false,
        error: `Unsupported file format. Supported formats: ${this.supportedFormats.join(', ')}`
      };
    }

    return { valid: true };
  }

  async validateImportData(importData) {
    // Check required structure
    if (!importData.tripData || !importData.tripData.trip) {
      return { valid: false, error: 'Missing trip information' };
    }

    if (!importData.tripData.trip.name) {
      return { valid: false, error: 'Trip name is required' };
    }

    // Check version compatibility
    if (importData.manifest?.exportMetadata?.exportVersion) {
      const version = importData.manifest.exportMetadata.exportVersion;
      if (!this.supportedVersions.includes(version)) {
        return {
          valid: false,
          error: `Unsupported export version: ${version}. Supported versions: ${this.supportedVersions.join(', ')}`
        };
      }
    }

    // Validate data arrays
    const arrays = ['days', 'places', 'accommodation', 'travel', 'documents'];
    for (const arrayName of arrays) {
      const data = importData.tripData[arrayName];
      if (data && !Array.isArray(data)) {
        return { valid: false, error: `Invalid ${arrayName} data format` };
      }
    }

    // Check for reasonable data sizes
    const totalItems = this.countImportedItems(importData);
    if (totalItems === 0) {
      return { valid: false, error: 'No importable data found' };
    }

    return { valid: true };
  }

  // validateImportContext removed - simplified import doesn't use modes
  // Context validation now done inline in importTrip() method

  /**
   * Conflict checking and resolution
   */
  async checkForConflicts(importData, context, importMode) {
    console.log('Checking for conflicts...');

    const conflicts = [];

    if (importMode === 'merge') {
      // Check for potential duplicates
      // In real implementation, would check against existing data
      const hasNameConflicts = Math.random() > 0.7; // 30% chance for demo

      if (hasNameConflicts) {
        conflicts.push({
          type: 'duplicate_names',
          message: 'Some places or accommodations may already exist',
          severity: 'warning',
          canProceed: true
        });
      }
    }

    if (importMode === 'replace') {
      // Warn about data loss
      conflicts.push({
        type: 'data_replacement',
        message: 'This will permanently replace all current trip data',
        severity: 'critical',
        canProceed: true,
        requiresConfirmation: true
      });
    }

    return {
      hasConflicts: conflicts.length > 0,
      conflicts: conflicts,
      canProceed: !conflicts.some(c => c.severity === 'critical' && !c.canProceed)
    };
  }

  /**
   * Helper methods
   */
  generateContentSummary(tripData) {
    return {
      days: tripData.days?.length || 0,
      places: tripData.places?.length || 0,
      accommodation: tripData.accommodation?.length || 0,
      travel: tripData.travel?.length || 0,
      documents: tripData.documents?.length || 0
    };
  }

  countImportedItems(importData) {
    const summary = importData.contentSummary;
    return (summary?.days || 0) +
           (summary?.places || 0) +
           (summary?.accommodation || 0) +
           (summary?.travel || 0) +
           (summary?.documents || 0);
  }

  async getCurrentTripItemCount(context) {
    // In real implementation, would count current trip items
    return Math.floor(Math.random() * 20) + 5; // 5-25 items for demo
  }

  async simulateImportProcess(mode) {
    const delays = {
      new: [300, 500, 400, 600, 350],
      merge: [400, 600, 500, 700, 450],
      replace: [200, 800, 600, 900, 550]
    };

    const steps = delays[mode] || delays.new;

    for (const delay of steps) {
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  readFileAsText(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => resolve(event.target.result);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }

  /**
   * Notification methods
   */
  showNotification(type, data) {
    const messages = {
      started: `Starting import from ${data}...`,
      completed: `Import completed successfully: ${data?.tripName || 'Trip imported'}`,
      error: `Import error: ${data}`
    };

    console.log(messages[type]);

    if (this.notifications[type]) {
      this.notifications[type](messages[type]);
    }
  }

  setNotificationHandlers(handlers) {
    this.notifications = { ...this.notifications, ...handlers };
  }

  /**
   * Get supported file formats with descriptions
   */
  getSupportedFormats() {
    return [
      {
        extension: 'zip',
        label: 'ZIP Archive',
        description: 'Complete trip backup with documents and media',
        mimeType: 'application/zip',
        features: ['Complete data', 'Documents included', 'Media files', 'Full restore capability']
      },
      {
        extension: 'json',
        label: 'JSON File',
        description: 'Trip data only (no documents or media)',
        mimeType: 'application/json',
        features: ['Trip data only', 'Lightweight', 'Fast processing', 'Universal format']
      }
    ];
  }

  /**
   * Get import mode options with descriptions
   */
  getImportModes() {
    return [
      {
        value: 'new',
        label: 'New Trip',
        description: 'Import as a completely new trip',
        icon: 'add',
        recommended: true,
        features: ['Creates new trip', 'No conflicts', 'All data imported', 'Safe option']
      },
      {
        value: 'merge',
        label: 'Merge with Current',
        description: 'Add imported data to current trip',
        icon: 'merge',
        warning: 'May create duplicate entries',
        features: ['Adds to current trip', 'Preserves existing data', 'May have duplicates', 'Smart conflict detection']
      },
      {
        value: 'replace',
        label: 'Replace Current',
        description: 'Replace all current trip data',
        icon: 'swap',
        warning: 'This will permanently delete current trip data',
        requiresConfirmation: true,
        features: ['Replaces all data', 'Keeps trip ID', 'Permanent action', 'Backup recommended']
      }
    ];
  }
}

export default new TripImportService();